import apiClient, { handleApiError } from '../lib/api-client';
import { API_ENDPOINTS } from '../config/api';

export interface ChatMessage {
  id: string;
  question: string;
  answer: string;
  createdAt: string;
}

export interface ChatRequest {
  question: string;
}

export interface FoodItemAnalysis {
  name: string;
  calories: number;
  carbs?: number;
  protein?: number;
  fat?: number;
}

export interface AnalyzeFoodImageResult {
  foodItems: FoodItemAnalysis[];
  totalCalories: number;
  totalCarbs?: number;
  totalProtein?: number;
  totalFat?: number;
}

export const aiAssistantService = {
  /**
   * Calls POST /ai/analyze-food-image (vision AI) with base64 image.
   * Accepts full data URL (data:image/...;base64,...) or raw base64; backend accepts both.
   */
  async analyzeFoodImage(imageBase64: string): Promise<AnalyzeFoodImageResult> {
    try {
      // Send as-is; backend accepts data URL or raw base64
      const payload = { imageBase64 };
      const response = await apiClient.post<{ data?: AnalyzeFoodImageResult; success?: boolean }>(
        API_ENDPOINTS.AI.ANALYZE_FOOD_IMAGE,
        payload
      );
      const body = response.data;
      const result =
        (body && typeof body === 'object' && 'data' in body && body.data) ||
        (body as unknown as AnalyzeFoodImageResult);
      if (!result || !Array.isArray(result.foodItems)) {
        throw new Error('Invalid response from AI analyze-food-image API');
      }
      return result as AnalyzeFoodImageResult;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async sendMessage(data: ChatRequest): Promise<{ answer: string; messageId?: string }> {
    try {
      const response = await apiClient.post<{ data: { answer: string; id?: string } }>(
        API_ENDPOINTS.AI.CHAT,
        data
      );
      const result = response.data.data || response.data;
      return {
        answer: result.answer || '',
        messageId: result.id || (result as any).conversationId,
      };
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getHistory(params?: { limit?: number; offset?: number }): Promise<ChatMessage[]> {
    try {
      const response = await apiClient.get<{ data: { data: ChatMessage[] } }>(
        API_ENDPOINTS.AI.HISTORY,
        { params }
      );
      return response.data.data?.data || response.data.data || [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};

